import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useProducts } from "@/hooks/useProducts";
import { usePriceBooks } from "@/hooks/usePriceBooks";
import { useMeasurements } from "@/hooks/useMeasurements";
import { useEstimateLineItems } from "@/hooks/useEstimateLineItems";
import { Search, Plus, Zap, AlertCircle, Package } from "lucide-react";
import { QuickAddMaterialsDialog } from "./QuickAddMaterialsDialog";
import { generateLineItemsFromMeasurements, validateMeasurements, calculateSquares, type Product as EstimateProduct, type CalculationConfig } from "@/utils/estimateCalculations";
import { shouldReplaceProduct } from "@/utils/productCategoryMapping";
import type { GeneratedLineItem } from "@/utils/estimateCalculations";

interface ProductSelectorProps {
  county: string;
  estimateId?: string;
  existingLineItemsCount: number;
  onAddLineItem: (item: any) => void;
  onAddMultipleLineItems: (items: any[]) => void;
  onReplaceOrAddLineItem: (data: { lineItem: any; existingItemId?: string }) => void;
}

const ProductSelector = ({ 
  county, 
  estimateId,
  existingLineItemsCount,
  onAddLineItem, 
  onAddMultipleLineItems,
  onReplaceOrAddLineItem,
}: ProductSelectorProps) => {
  const { products } = useProducts();
  const { priceBooks, productPrices } = usePriceBooks();
  const { measurements } = useMeasurements(estimateId);
  const { lineItems } = useEstimateLineItems(estimateId);
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [generatedItems, setGeneratedItems] = useState<GeneratedLineItem[]>([]);

  const activePriceBook = priceBooks?.find(
    (pb) => pb.county === county && pb.is_active
  );

  const measurement = measurements && measurements.length > 0 ? measurements[0] : null;
  const hasValidMeasurements = measurement && validateMeasurements(measurement).valid;
  const showMissingMaterialsWarning = 
    hasValidMeasurements && existingLineItemsCount === 0;

  const handleQuickAddClick = () => {
    if (!measurement || !activePriceBook || !products || !productPrices) return;

    // Cast products to EstimateProduct type with proper calculation_config handling
    const typedProducts: EstimateProduct[] = products.map(p => ({
      ...p,
      calculation_config: p.calculation_config as unknown as CalculationConfig | null
    }));

    const items = generateLineItemsFromMeasurements(
      measurement,
      activePriceBook.id,
      typedProducts,
      productPrices
    );

    setGeneratedItems(items);
    setShowQuickAdd(true);
  };

  const handleAddGeneratedItems = (items: GeneratedLineItem[]) => {
    const itemsWithEstimateId = items.map(item => ({
      ...item,
      estimate_id: estimateId,
    }));
    onAddMultipleLineItems(itemsWithEstimateId);
  };

  const categories = [
    "Shingles",
    "Underlayment",
    "Ridge Cap",
    "Ice & Water",
    "Starter Strip",
    "Drip Edge",
    "Ventilation",
    "Flashing",
    "Nails",
    "Labor",
  ];

  const filteredProducts = products?.filter((product) =>
    product.name.toLowerCase().includes(search.toLowerCase()) ||
    product.sku.toLowerCase().includes(search.toLowerCase())
  );

  const getProductsByCategory = (category: string) => {
    return filteredProducts?.filter((p) => p.category === category) || [];
  };

  const getPrice = (productId: string) => {
    const price = productPrices?.find(
      (pp) => pp.product_id === productId && pp.price_book_id === activePriceBook?.id
    );
    return price?.unit_price || 0;
  };

  const handleAddProduct = (product: any) => {
    const price = getPrice(product.id);
    let quantity = quantities[product.id] || 1;
    let unit = product.unit;

    // Auto-calculate quantity for roll-based products if measurements exist
    if (measurement && product.category) {
      const specs = product.specifications as any;
      
      // Underlayment: calculate rolls from roof area
      if (product.category.toLowerCase().includes('underlayment') && measurement.area_sqft) {
        const sqftPerRoll = specs?.coverage_per_unit || 1000;
        const wasteFactor = (measurement as any).waste_factor || 1.10;
        quantity = Math.ceil((measurement.area_sqft * wasteFactor) / sqftPerRoll);
        unit = 'roll';
      }
      
      // Ice & Water Shield: calculate rolls from linear feet
      if ((product.category.toLowerCase().includes('ice') || 
           product.category.toLowerCase().includes('water') ||
           product.name.toLowerCase().includes('ice')) && 
          (measurement.valley_lf || measurement.eave_lf)) {
        const rollCoverage = specs?.roll_dimensions?.coverage || 99;
        const wasteFactor = (measurement as any).waste_factor || 1.10;
        const totalLF = (measurement.valley_lf || 0) + (measurement.eave_lf || 0) + (measurement.flashing_lf || 0);
        const totalSqft = totalLF * 3 * wasteFactor;
        quantity = Math.ceil(totalSqft / rollCoverage);
        unit = 'roll';
      }
    }

    // Check if this product should replace an existing auto-generated item
    const existingItem = shouldReplaceProduct(
      product.category,
      product.name,
      lineItems || []
    );

    if (existingItem) {
      // Replace: keep quantity from measurements, update product details
      const lineTotal = quantity * price;
      
      onReplaceOrAddLineItem({
        lineItem: {
          estimate_id: estimateId,
          product_id: product.id,
          description: product.name,
          quantity,
          unit,
          unit_price: price,
          discount_percent: existingItem.discount_percent,
          discount_amount: existingItem.discount_amount,
          line_total: lineTotal,
          calculation_note: existingItem.calculation_note,
        },
        existingItemId: existingItem.id,
      });
    } else {
      // Add new item with calculated/manual quantity
      const lineTotal = quantity * price;
      
      onReplaceOrAddLineItem({
        lineItem: {
          estimate_id: estimateId,
          product_id: product.id,
          description: product.name,
          quantity,
          unit,
          unit_price: price,
          discount_percent: 0,
          discount_amount: 0,
          line_total: lineTotal,
        },
      });
    }

    setQuantities({ ...quantities, [product.id]: 1 });
  };

  return (
    <div className="space-y-6">
      {showMissingMaterialsWarning && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              No products added yet. Click <strong>Quick Add Materials</strong> to auto-populate based on measurements.
            </span>
            <Button 
              size="sm" 
              onClick={handleQuickAddClick}
              disabled={!activePriceBook}
            >
              <Zap className="h-4 w-4 mr-2" />
              Quick Add
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <QuickAddMaterialsDialog
        open={showQuickAdd}
        onOpenChange={setShowQuickAdd}
        lineItems={generatedItems}
        totalSquares={measurement ? calculateSquares(measurement.area_sqft || 0) : 0}
        onAddItems={handleAddGeneratedItems}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Select Products</CardTitle>
            <div className="flex items-center gap-2">
              {hasValidMeasurements && (
                <Button 
                  onClick={handleQuickAddClick}
                  disabled={!activePriceBook}
                  variant="default"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Quick Add Materials
                </Button>
              )}
              <Badge variant="secondary">
                Price Book: {activePriceBook?.name || "None"}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <Tabs defaultValue="Shingles">
            <TabsList className="grid grid-cols-5 lg:grid-cols-10">
              {categories.map((cat) => (
                <TabsTrigger key={cat} value={cat} className="text-xs">
                  {cat}
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map((category) => (
              <TabsContent key={category} value={category} className="space-y-2">
                {getProductsByCategory(category).map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent transition-colors"
                  >
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                        <Package className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{product.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {product.brand} • {product.sku}
                      </div>
                      <div className="text-sm font-medium mt-1">
                        ${getPrice(product.id)} / {product.unit}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        value={quantities[product.id] || 1}
                        onChange={(e) =>
                          setQuantities({
                            ...quantities,
                            [product.id]: parseInt(e.target.value) || 1,
                          })
                        }
                        className="w-20"
                      />
                      <Button onClick={() => handleAddProduct(product)} size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </div>
                ))}
                {getProductsByCategory(category).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No products in this category
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductSelector;
